import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import { Input } from "../../components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../components/ui/form";
import { useForm } from "react-hook-form";
import {
  addHealthRecordSchema,
  AddHealthRecordSchema,
} from "../../lib/validation/add-health-record";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { X } from "lucide-react";
import { useAppointmentData } from "../../contexts/appointment-data-context";

interface PopHealthRecordsProps {
    onClose: () => void;
  }

function PopAddHealthRecords({ onClose }: PopHealthRecordsProps) {
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirectUrl") || "/";
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const { data, setData } = useAppointmentData();

  const form = useForm<AddHealthRecordSchema>({
    resolver: zodResolver(addHealthRecordSchema),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = Array.from(e.target.files || []);
    const uniqueFiles = newFiles.filter(
      (newFile) =>
        !selectedFiles?.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size,
        ),
    );

    if (uniqueFiles.length > 0)
      setSelectedFiles((prevFiles) => [...(prevFiles ?? []), ...uniqueFiles]);

    if (fileInputRef.current)
      fileInputRef.current.value = null as unknown as string;
  }

  function handleRemoveFile(fileToRemove: File) {
    setSelectedFiles((prevFiles) =>
      (prevFiles ?? []).filter(
        (file) =>
          !(file.name === fileToRemove.name && file.size === fileToRemove.size),
      ),
    );
  }

  function handleAddRecord({ physician }: AddHealthRecordSchema) {
    setData({
      ...data,
      healthRecordPhysician: physician,
      healthRecords: selectedFiles,
      branchDetails: data?.branchDetails ?? [],
    });
    onClose();
  }

  return (
    <div className="container flex min-h-screen max-w-md flex-col">
      <div className="mb-4 flex items-center gap-2 sm:mb-8">

      <Button variant="ghost" size="icon"   onClick={() => onClose()}
 className="relative group">
          <BackIcon />
           <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:flex items-center p-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
             Back
          </span>
      </Button>

        <h1 className="text-sm font-bold">Add Record</h1>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleAddRecord)}
          className="space-y-8"
        >
          <div>
            <label
              htmlFor="health-record-file"
              className="inline-flex h-12 w-full cursor-pointer items-center justify-between rounded-md border border-input px-3 py-2 text-xs"
            >
              <span>Upload Record</span>
              <span className="font-bold text-primary">Select</span>
            </label>
            <input
              type="file"
              ref={fileInputRef}
              className="sr-only"
              id="health-record-file"
              multiple
              accept=".pdf,.jpeg,.jpg,.png"
              onChange={handleFileChange}
            />
            <ul className="mt-1 flex flex-wrap gap-2">
              {selectedFiles?.map((file) => (
                <li
                  key={file.name}
                  className="flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-sm"
                >
                  <p>{file.name}</p>
                  <button
                    type="button"
                    className="p-1"
                    onClick={() => handleRemoveFile(file)}
                  >
                    <X className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-muted-foreground sm:text-sm">
              Please upload 1 record with multiple pages at a time
            </p>
          </div>

          <FormField
            control={form.control}
            name="physician"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="Doctor Name or Lab Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="bottom-5 left-0 w-full space-y-4 max-sm:fixed max-sm:px-4">
            <Button
              type="submit"
              className="w-full font-bold shadow-xl"
              size="lg"
            >
              Upload
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default PopAddHealthRecords;
